import { Suspense, lazy, useEffect } from 'react'
import { useSearchParams } from 'react-router-dom'

const ReadinessShell = lazy(() => import('../readiness/ReadinessShell'))

/**
 * The planning schedule, as a page in its own right.
 *
 * It used to be a display mode of VisualizationPage at /visualization/schedule, which made the
 * canvas its parent in both the URL and the code. It is not part of the canvas: it shares the
 * hierarchy and nothing else — no React Flow, no shared state, its own endpoint. So it has its own
 * route and its own page, and the canvas can change without this having to be considered.
 */
function LoadingFallback() {
  return (
    <div className="flex h-full items-center justify-center">
      <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent" />
    </div>
  )
}

export function SchedulePage() {
  const [searchParams] = useSearchParams()

  // Fill the main area, the same way the canvas page does — the chart is a workspace, not a
  // document in a padded container.
  useEffect(() => {
    const main = document.querySelector('main')
    const container = main?.querySelector(':scope > div') as HTMLElement | null
    if (main) { main.style.overflow = 'hidden'; main.style.padding = '0' }
    if (container) { container.style.padding = '0'; container.style.maxWidth = 'none'; container.style.height = '100%' }
    return () => {
      if (main) { main.style.overflow = ''; main.style.padding = '' }
      if (container) { container.style.padding = ''; container.style.maxWidth = ''; container.style.height = '' }
    }
  }, [])

  // Everything else — the apex, which of the five views is showing, and the mode — lives in the
  // URL and is read by the shell, so a Readiness view is a link someone can send.
  const scenarioId = searchParams.get('scenarioId') || '1'

  return (
    <div style={{ width: '100%', height: '100%', overflow: 'hidden' }} className="flex flex-col">
      <Suspense fallback={<LoadingFallback />}>
        {/* Five readings of one plan. The shell owns the palette, the apex and the schedule; a
            live run is reached through Recovery Manager, which mounts the chart with its own
            data. */}
        <ReadinessShell scenarioId={scenarioId} />
      </Suspense>
    </div>
  )
}

export default SchedulePage
